import React, { FC } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Label from '@mui/icons-material/Label';
import { SimpleTreeView, TreeItem, TreeItemProps, treeItemClasses } from '@mui/x-tree-view';
import DeleteIcon from '@mui/icons-material/Delete';
import { SCUITreeViewProps } from '../../types/interfaces/CampaingTypes';

declare module 'react' {
    interface CSSProperties {
        '--tree-view-color'?: string;
        '--tree-view-bg-color'?: string;
    }
}

type StyledTreeItemProps = TreeItemProps & {
    bgColor?: string;
    bgColorForDarkMode?: string;
    color?: string;
    colorForDarkMode?: string;
    labelIcon?: React.ElementType;
    labelInfo?: string;
    labelText: string;
};

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
    color: theme.palette.text.secondary,
    [`& .${treeItemClasses.content}`]: {
        color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        paddingRight: theme.spacing(1),
        fontWeight: theme.typography.fontWeightMedium,
        '&.Mui-expanded': {
            fontWeight: theme.typography.fontWeightRegular,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
            backgroundColor: `var(--tree-view-bg-color, ${'#e8f0fe'})`,
            color: `var(--tree-view-color, ${'#1a73e8'})`,
        },
        [`& .${treeItemClasses.label}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
        },
    },
})) as unknown as typeof TreeItem;

const SCUITreeView: FC<SCUITreeViewProps> = ({ folders, onNodeSelect, defaultExpanded, selected }) => {
    const StyledTreeItem = React.forwardRef(function StyledTreeItem(
        props: StyledTreeItemProps,
        ref: React.Ref<HTMLLIElement>
    ) {
        const theme = useTheme();
        const {
            bgColor,
            color,
            labelIcon: LabelIcon,
            labelInfo,
            labelText,
            colorForDarkMode,
            bgColorForDarkMode,
            ...other
        } = props;

        const styleProps = {
            '--tree-view-color': theme.palette.mode !== 'dark' ? color : colorForDarkMode,
            '--tree-view-bg-color': theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
        };

        return (
            <StyledTreeItemRoot
                label={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 0.5,
                            pr: 0,
                        }}
                    >
                        {LabelIcon && <LabelIcon color="inherit" style={{ marginRight: 4 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 'inherit', flexGrow: 1 }}>
                            {labelText}
                        </Typography>
                        <Typography variant="caption" color="inherit">
                            {labelInfo}
                        </Typography>
                    </Box>
                }
                style={styleProps}
                {...other}
                ref={ref}
            />
        );
    });

    const renderTree = (node: any) => {
        let labelIcon = undefined;
        if (node.parentId == null) {
            labelIcon = node.type == 2 ? DeleteIcon : Label; //TODO: enum olacak. Junk Folder: 2, UserFolder: 1
        }

        return (
            <StyledTreeItem itemId={String(node.id)} labelText={node.name} labelIcon={labelIcon}>
                {Array.isArray(node.children) ? node.children.map((childNode: any) => renderTree(childNode)) : null}
            </StyledTreeItem>
        );
    };

    return (
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        <SimpleTreeView
            aria-label="treeview"
            sx={{
                // height: 264,
                flexGrow: 1,
                maxWidth: 400,
                overflowY: 'auto',
            }}
            onItemSelectionToggle={onNodeSelect}
            defaultExpandedItems={defaultExpanded}
            selectedItems={selected}
        >
            {folders.map((node: any) => renderTree(node))}
        </SimpleTreeView>
    );
};

export default SCUITreeView;
